import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";

import styled from "styled-components";

import Image from "components/atoms/Image";
import Button from "components/atoms/button";
import Text from "components/atoms/text/Text";

import AbstractModal from "components/page/modal/AbstractModal";

import Constants from "utils/constants";

import moment from "moment";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

// 전체 모달 컨테이너
const ModalContent = styled.div`
    width: 100%;

    @media print {
        padding: 20px;
    }
`;

const Row = styled.div`
    display: flex;
    flex-wrap: ${(props) => props.$wrap || "nowrap"};
    flex-direction: row;
    justify-content: ${(props) => props.$justifyContent || "space-between"};
    align-items: ${(props) => props.$alignItems || "flex-start"};
    gap: ${(props) => props.$gap || "0"};
    margin: ${(props) => props.$margin || "16px 0 0 0"};
    height: auto;
    width: 100%;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
    margin: 24px 0;
`;

const PaymentTitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--color-SubBlack);
`;

const PaymentAmountText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
`;

const TitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const ContentText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const PaymentInfoBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    background-color: var(--color-InfoBox);
    border-radius: 2px;
    padding: 12px;
    margin-top: 16px;
`;

const PaymentInfoSummary = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`;

const PaymentInfoBoxRow = styled(Row)`
    margin: 0;
`;

const PaymentInfoBoxTitleText = styled(TitleText)``;

const PaymentInfoBoxSubTitleText = styled(Text)`
    margin: 8px 0 0 0;
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const PaymentInfoBoxContentText = styled(ContentText)`
    flex: 0 0 auto;
    margin-left: 12px;
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const SmallText = styled(Text)`
    margin: 0;
    font-size: 1.2rem;
    text-align: ${(props) => props.textAlign || "right"};
    line-height: 1.4;
    color: var(--color-Grey1);
`;

const Footer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 32px;
    flex: 0 0 auto;
`;

const SpacePaymentReceiptModal = (props) => {
    const { exitModal } = props;
    const printRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => console.log("Print job completed!"),
    });

    const paymentReceipt = props.paymentHistory;
    const isMemberAdd = paymentReceipt?.payment_type === Constants.PAYMENT_TYPE_DIRECT_ADD;
    const memberCount = paymentReceipt?.member_count || 1;
    const targetMonth = Math.round(
        moment(paymentReceipt?.ended_at).diff(moment(paymentReceipt?.started_at), "months", true),
    );
    const totalAmount = isMemberAdd
        ? paymentReceipt?.amount || 0
        : (paymentReceipt?.plan?.price || 0) * (targetMonth || 1) * memberCount;

    const receiptFooter = (
        <Footer>
            <Button
                onlyText
                onClick={handlePrint}
                margin={""}
                width={"160px"}
                height={"32px"}
                bgColor={"var(--color-Button2)"}
                hoverBgColor={"var(--color-ButtonHover2)"}
                fontColor={"var(--color-White)"}
                buttonText={"인쇄"}
            />
        </Footer>
    );

    return (
        <AbstractModal modalTitle="" width={500} height={640} exitModal={exitModal} footer={receiptFooter}>
            <Container>
                <ModalContent ref={printRef}>
                    <Row>
                        <Image name={"headerLogo"} />
                        <SmallText>
                            (주) 데이탄소프트
                            <br />
                            제주특별자치도 제주시 도령북길 31, 2층
                            <br />
                            prep@datansoft.com
                        </SmallText>
                    </Row>

                    <Divider />

                    <Row>
                        <PaymentTitleText>총 결제 금액</PaymentTitleText>
                        <PaymentAmountText>₩{paymentReceipt?.amount.toLocaleString("ko-KR")}</PaymentAmountText>
                    </Row>

                    <PaymentInfoBox>
                        <PaymentInfoBoxRow alignItems={"flex-start"} wrap={"wrap"} gap={"8px"}>
                            <PaymentInfoSummary>
                                <PaymentInfoBoxTitleText>{paymentReceipt?.description}</PaymentInfoBoxTitleText>
                                <PaymentInfoBoxSubTitleText>
                                    이용 기간: {moment(paymentReceipt?.started_at).format("YYYY.MM.DD")} ~{" "}
                                    {moment(paymentReceipt?.ended_at).format("YYYY.MM.DD")}
                                </PaymentInfoBoxSubTitleText>
                                {isMemberAdd ? (
                                    <PaymentInfoBoxSubTitleText>추가 멤버: {memberCount}명</PaymentInfoBoxSubTitleText>
                                ) : (
                                    <PaymentInfoBoxSubTitleText>인원: {memberCount}명</PaymentInfoBoxSubTitleText>
                                )}
                                <PaymentInfoBoxSubTitleText></PaymentInfoBoxSubTitleText>
                            </PaymentInfoSummary>
                            <PaymentInfoBoxContentText>
                                ₩{totalAmount.toLocaleString("ko-KR")}
                            </PaymentInfoBoxContentText>
                        </PaymentInfoBoxRow>
                        {!isMemberAdd &&
                            paymentReceipt?.discount_policy?.filter(
                                (p) => p.internal_code === Constants.THREE_MONTH_DISCOUNT_POLICY,
                            ).length > 0 && (
                                <PaymentInfoBoxRow wrap={"wrap"} gap={"8px"}>
                                    <PaymentInfoBoxTitleText>장기 결제 할인</PaymentInfoBoxTitleText>
                                    <PaymentInfoBoxContentText>
                                        - ₩{(totalAmount * 0.1).toLocaleString("ko-KR")}
                                    </PaymentInfoBoxContentText>
                                </PaymentInfoBoxRow>
                            )}
                        {!isMemberAdd &&
                            paymentReceipt?.discount_policy?.filter(
                                (p) => p.internal_code === Constants.GROUP_DISCOUNT_POLICY,
                            ).length > 0 && (
                                <PaymentInfoBoxRow wrap={"wrap"} gap={"8px"}>
                                    <PaymentInfoBoxTitleText>그룹 결제 할인</PaymentInfoBoxTitleText>
                                    <PaymentInfoBoxContentText>
                                        - ₩{(totalAmount * 0.1).toLocaleString("ko-KR")}
                                    </PaymentInfoBoxContentText>
                                </PaymentInfoBoxRow>
                            )}
                    </PaymentInfoBox>

                    <Row $margin={"12px 0 0 0"}>
                        <SmallText textAlign={"left"}>표시된 모든 금액은 부가세(VAT)가 포함된 금액입니다.</SmallText>
                    </Row>
                    <Divider />
                    <Row>
                        <TitleText>결제번호</TitleText>
                        <ContentText>{paymentReceipt?.iamport_id}</ContentText>
                    </Row>

                    <Row>
                        <TitleText>결제일</TitleText>
                        <ContentText>{moment(paymentReceipt?.created_at).format("YYYY.MM.DD HH:mm:ss")}</ContentText>
                    </Row>
                    <Row>
                        <TitleText>결제 상태</TitleText>
                        <ContentText>결제 완료</ContentText>
                    </Row>
                    <Row>
                        <TitleText>결제 수단</TitleText>
                        <ContentText>
                            {paymentReceipt?.card?.card_name} {paymentReceipt?.card?.card_number}
                        </ContentText>
                    </Row>
                    <Divider />
                    <Row $margin={"40px 0 60px 0"}>
                        <TitleText>위의 결제 금액을 영수하였습니다.</TitleText>
                        <SmallText>계정: {props.user?.email}</SmallText>
                    </Row>
                </ModalContent>
            </Container>
        </AbstractModal>
    );
};

export default SpacePaymentReceiptModal;
